import { authAPI } from './api';

export interface CompanyFeature {
  featureKey: string;
  enabled: boolean;
  config: Record<string, unknown>;
  updatedAt?: string;
}

export interface CompanyBranding {
  logoUrl?: string;
  faviconUrl?: string;
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
  customCss?: string;
  updatedAt?: string;
}

export interface CompanyWorkflow {
  id?: string;
  module: string;
  definition: Record<string, unknown>;
  isActive: boolean;
  updatedAt?: string;
}

export interface CompanyCustomField {
  id?: string;
  module: string;
  fieldKey: string;
  label: string;
  fieldType: string;
  required: boolean;
  position: number;
  config: Record<string, unknown>;
  validations: Record<string, unknown>;
  isActive: boolean;
  updatedAt?: string;
}

export interface CompanyNotificationTemplate {
  id?: string;
  channel: string;
  templateKey: string;
  subject?: string;
  body: string;
  dataSchema: Record<string, unknown>;
  isActive: boolean;
  updatedAt?: string;
}

export interface CompanyLimit {
  id: string;
  company_id: string;
  max_users: number;
  max_employees: number;
  max_clients: number;
  current_users: number;
  current_employees: number;
  current_clients: number;
  is_active: boolean;
  notes?: string;
  updated_at: string;
}

export interface CompanyLimitRequest {
  max_users: number;
  max_employees: number;
  max_clients: number;
  is_active?: boolean;
  notes?: string;
}

export interface UsageStats {
  resource_type: 'users' | 'employees' | 'clients';
  current: number;
  limit: number;
  percentage: number;
  is_exceeded: boolean;
}

type FeatureDTO = {
  feature_key: string;
  enabled: boolean;
  config?: Record<string, unknown>;
  updated_at?: string;
};

type BrandingDTO = {
  logo_url?: string;
  favicon_url?: string;
  primary_color?: string;
  secondary_color?: string;
  accent_color?: string;
  custom_css?: string;
  updated_at?: string;
};

type WorkflowDTO = {
  id?: string;
  module: string;
  definition?: Record<string, unknown>;
  is_active: boolean;
  updated_at?: string;
};

type CustomFieldDTO = {
  id?: string;
  module: string;
  field_key: string;
  label: string;
  field_type: string;
  required: boolean;
  position: number;
  config?: Record<string, unknown>;
  validations?: Record<string, unknown>;
  is_active: boolean;
  updated_at?: string;
};

type TemplateDTO = {
  id?: string;
  channel: string;
  template_key: string;
  subject?: string;
  body: string;
  data_schema?: Record<string, unknown>;
  is_active: boolean;
  updated_at?: string;
};

const mapFeature = (dto: FeatureDTO): CompanyFeature => ({
  featureKey: dto.feature_key,
  enabled: dto.enabled,
  config: dto.config || {},
  updatedAt: dto.updated_at,
});

const mapBranding = (dto: BrandingDTO): CompanyBranding => ({
  logoUrl: dto.logo_url || undefined,
  faviconUrl: dto.favicon_url || undefined,
  primaryColor: dto.primary_color || undefined,
  secondaryColor: dto.secondary_color || undefined,
  accentColor: dto.accent_color || undefined,
  customCss: dto.custom_css || undefined,
  updatedAt: dto.updated_at,
});

const mapWorkflow = (dto: WorkflowDTO): CompanyWorkflow => ({
  id: dto.id,
  module: dto.module,
  definition: dto.definition || {},
  isActive: dto.is_active,
  updatedAt: dto.updated_at,
});

const mapCustomField = (dto: CustomFieldDTO): CompanyCustomField => ({
  id: dto.id,
  module: dto.module,
  fieldKey: dto.field_key,
  label: dto.label,
  fieldType: dto.field_type,
  required: dto.required,
  position: dto.position,
  config: dto.config || {},
  validations: dto.validations || {},
  isActive: dto.is_active,
  updatedAt: dto.updated_at,
});

const mapTemplate = (dto: TemplateDTO): CompanyNotificationTemplate => ({
  id: dto.id,
  channel: dto.channel,
  templateKey: dto.template_key,
  subject: dto.subject,
  body: dto.body,
  dataSchema: dto.data_schema || {},
  isActive: dto.is_active,
  updatedAt: dto.updated_at,
});

export const fetchCompanyFeatures = async (companyId: string): Promise<CompanyFeature[]> => {
  const { data } = await authAPI.get<FeatureDTO[]>(`/companies/${companyId}/features`);
  return Array.isArray(data) ? data.map(mapFeature) : [];
};

export const upsertCompanyFeature = async (
  companyId: string,
  payload: { featureKey: string; enabled: boolean; config?: Record<string, unknown> }
): Promise<CompanyFeature> => {
  const { data } = await authAPI.put<FeatureDTO>(`/admin/companies/${companyId}/features`, {
    feature_key: payload.featureKey,
    enabled: payload.enabled,
    config: payload.config ?? {},
  });
  return mapFeature(data);
};

export const fetchCompanyBranding = async (companyId: string): Promise<CompanyBranding | null> => {
  try {
    const { data } = await authAPI.get<BrandingDTO>(`/companies/${companyId}/branding`);
    return mapBranding(data);
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

export const updateCompanyBranding = async (
  companyId: string,
  payload: CompanyBranding
): Promise<CompanyBranding> => {
  const { data } = await authAPI.put<BrandingDTO>(`/admin/companies/${companyId}/branding`, {
    logo_url: payload.logoUrl,
    favicon_url: payload.faviconUrl,
    primary_color: payload.primaryColor,
    secondary_color: payload.secondaryColor,
    accent_color: payload.accentColor,
    custom_css: payload.customCss,
  });
  return mapBranding(data);
};

export const fetchCompanyWorkflows = async (companyId: string): Promise<CompanyWorkflow[]> => {
  const { data } = await authAPI.get<WorkflowDTO[]>(`/companies/${companyId}/workflows`);
  return Array.isArray(data) ? data.map(mapWorkflow) : [];
};

export const upsertCompanyWorkflow = async (
  companyId: string,
  module: string,
  payload: { definition: Record<string, unknown>; isActive: boolean }
): Promise<CompanyWorkflow> => {
  const { data } = await authAPI.put<WorkflowDTO>(
    `/admin/companies/${companyId}/workflows/${encodeURIComponent(module)}`,
    {
      module,
      definition: payload.definition,
      is_active: payload.isActive,
    }
  );
  return mapWorkflow(data);
};

export const deleteCompanyWorkflow = (companyId: string, module: string) =>
  authAPI.delete(`/admin/companies/${companyId}/workflows/${encodeURIComponent(module)}`);

export const fetchCompanyCustomFields = async (
  companyId: string,
  module?: string
): Promise<CompanyCustomField[]> => {
  const { data } = await authAPI.get<CustomFieldDTO[]>(`/companies/${companyId}/custom-fields`, {
    params: module ? { module } : undefined,
  });
  return Array.isArray(data) ? data.map(mapCustomField) : [];
};

export const upsertCompanyCustomField = async (
  companyId: string,
  payload: Omit<CompanyCustomField, 'id' | 'updatedAt'>
): Promise<CompanyCustomField> => {
  const { data } = await authAPI.put<CustomFieldDTO>(`/admin/companies/${companyId}/custom-fields`, {
    module: payload.module,
    field_key: payload.fieldKey,
    label: payload.label,
    field_type: payload.fieldType,
    required: payload.required,
    position: payload.position,
    config: payload.config,
    validations: payload.validations,
    is_active: payload.isActive,
  });
  return mapCustomField(data);
};

export const deleteCompanyCustomField = (companyId: string, module: string, fieldKey: string) =>
  authAPI.delete(
    `/admin/companies/${companyId}/custom-fields/${encodeURIComponent(module)}/${encodeURIComponent(fieldKey)}`
  );

export const fetchCompanyNotificationTemplates = async (
  companyId: string,
  channel?: string
): Promise<CompanyNotificationTemplate[]> => {
  const { data } = await authAPI.get<TemplateDTO[]>(`/companies/${companyId}/notification-templates`, {
    params: channel ? { channel } : undefined,
  });
  return Array.isArray(data) ? data.map(mapTemplate) : [];
};

export const upsertCompanyNotificationTemplate = async (
  companyId: string,
  payload: Omit<CompanyNotificationTemplate, 'id' | 'updatedAt'>
): Promise<CompanyNotificationTemplate> => {
  const { data } = await authAPI.put<TemplateDTO>(`/admin/companies/${companyId}/notification-templates`, {
    channel: payload.channel,
    template_key: payload.templateKey,
    subject: payload.subject,
    body: payload.body,
    data_schema: payload.dataSchema,
    is_active: payload.isActive,
  });
  return mapTemplate(data);
};

export const deleteCompanyNotificationTemplate = (
  companyId: string,
  channel: string,
  templateKey: string
) =>
  authAPI.delete(
    `/admin/companies/${companyId}/notification-templates/${encodeURIComponent(channel)}/${encodeURIComponent(
      templateKey
    )}`
  );

// Company Limits API
type LimitDTO = {
  id: string;
  company_id: string;
  max_users: number;
  max_employees: number;
  max_clients: number;
  current_users: number;
  current_employees: number;
  current_clients: number;
  is_active: boolean;
  notes?: string;
  updated_at: string;
};

const mapLimit = (dto: LimitDTO): CompanyLimit => ({
  id: dto.id,
  company_id: dto.company_id,
  max_users: dto.max_users,
  max_employees: dto.max_employees,
  max_clients: dto.max_clients,
  current_users: dto.current_users,
  current_employees: dto.current_employees,
  current_clients: dto.current_clients,
  is_active: dto.is_active,
  notes: dto.notes,
  updated_at: dto.updated_at,
});

export const fetchCompanyLimit = async (companyId: string): Promise<CompanyLimit> => {
  const { data } = await authAPI.get<LimitDTO>(`/admin/companies/${companyId}/limits`);
  return mapLimit(data);
};

export const upsertCompanyLimit = async (
  companyId: string,
  payload: CompanyLimitRequest
): Promise<CompanyLimit> => {
  const { data } = await authAPI.put<LimitDTO>(`/admin/companies/${companyId}/limits`, {
    max_users: payload.max_users,
    max_employees: payload.max_employees,
    max_clients: payload.max_clients,
    is_active: payload.is_active,
    notes: payload.notes,
  });
  return mapLimit(data);
};

export const fetchCompanyUsageStats = async (companyId: string): Promise<UsageStats[]> => {
  const { data } = await authAPI.get<UsageStats[]>(`/admin/companies/${companyId}/limits/stats`);
  return Array.isArray(data) ? data : [];
};

